import { CalendarDotsIcon } from "@/components/icons/calendar-dots-icon";
import { ProgressUpIcon } from "@/components/icons/progress-up-icon";
import { SparkleIcon } from "@/components/icons/sparkle-icon";
import { FilledButton } from "@/components/ui/buttons/FilledButton";
import { getSafeAreaBottom } from "@/utils/safe-area-bottom";
import type { TrialPeriodLabel } from "@/utils/revenue-cat/trial-period";
import { LegalLinksFooter } from "../legal-links-footer";

interface TrialStep {
  label: string;
  title: string;
  body: React.ReactNode;
  icon: React.ReactNode;
}

interface FreeTrialOfferViewProps {
  monthlyPrice: string;
  /** Real store trial length, e.g. `{ noun: "1 week", adjective: "1-week" }`. */
  trialLabel: TrialPeriodLabel;
  /** Whether the start action is in flight (disables the CTA). */
  isBusy: boolean;
  /** Whether the CTA can be pressed at all (e.g. offerings resolved). */
  canStart: boolean;
  onStart: () => void;
  /** Optional purchase backdrop (committed flow only). */
  backdrop?: React.ReactNode;
}

/**
 * The approved "How your free trial works" soft paywall presentation. Purely
 * presentational — the trial-start action is injected via `onStart`, so both the
 * committed (RevenueCat) and backend (POST /subscriptions/trial) flows share it.
 */
export function FreeTrialOfferView({
  monthlyPrice,
  trialLabel,
  isBusy,
  canStart,
  onStart,
  backdrop,
}: FreeTrialOfferViewProps) {
  const steps: TrialStep[] = [
    {
      label: "STEP 1",
      title: "Start free today",
      body: `Get ${trialLabel.noun} to experience NutriCoach properly`,
      icon: <SparkleIcon className="h-[21.5px] w-[21.5px]" />,
    },
    {
      label: "STEP 2",
      title: "Begin making progress",
      body: "Start losing weight from day one, without stressing about calories.",
      icon: <ProgressUpIcon className="h-6 w-6" />,
    },
    {
      label: "STEP 3",
      title: "Decide before you pay",
      body: (
        <>
          We&apos;ll remind you before your trial ends. Then it&apos;s {monthlyPrice}/month unless
          you cancel.
        </>
      ),
      icon: <CalendarDotsIcon className="h-6 w-6" />,
    },
  ];

  return (
    <div className="fixed inset-0 z-[9999] flex justify-center overflow-hidden bg-[#271746] bg-[url('/assets/images/paywall-bg.webp')] bg-cover bg-bottom bg-no-repeat text-white">
      <div className="safe-area-top relative z-10 flex h-full w-full max-w-[450px] flex-col overflow-x-hidden overflow-y-auto px-9">
        {/* Headline */}
        <h1
          className="mt-14 text-center text-[42px] leading-[40px] text-white"
          style={{ fontFamily: "var(--font-crimson-text)", fontWeight: 600 }}
        >
          How your
          <br />
          <span className="text-[#DDC5FF] italic">free trial</span> works
        </h1>

        {/* Steps */}
        <div className="relative mt-16 flex flex-col gap-6">
          {/* Dotted connector behind the icon column */}
          <div
            aria-hidden="true"
            className="absolute top-[56px] bottom-[56px] left-[28px] w-px border-l border-dashed border-[#DDC5FF]/40"
          />
          {steps.map((step) => (
            <div key={step.label} className="relative flex items-start gap-[22px]">
              <div className="flex size-14 shrink-0 items-center justify-center rounded-full bg-white">
                {step.icon}
              </div>
              <div className="pt-0.5">
                <p className="text-[11px] leading-5 font-bold text-[#DDC5FF]">{step.label}</p>
                <p className="text-base leading-5 font-bold text-white">{step.title}</p>
                <p className="text-[13px] leading-5 text-white">{step.body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Flexible spacer — fills the gap the glow sits in, keeping the CTA
            cluster anchored above the bottom edge rather than flush to it. */}
        <div className="min-h-10 flex-1" />

        {/* Footer */}
        <div
          className="flex flex-col items-center gap-5"
          style={{ paddingBottom: `calc(${getSafeAreaBottom()} + 1.5rem)` }}
        >
          <p className="text-center text-[13px] leading-normal text-white">
            Try{" "}
            <span className="font-bold" style={{ fontFamily: "var(--font-sans-serif)" }}>
              {trialLabel.noun} for free
            </span>
            , then{" "}
            <span className="font-bold" style={{ fontFamily: "var(--font-sans-serif)" }}>
              {monthlyPrice}
            </span>
            /month.
          </p>

          <FilledButton
            color="white"
            borderRadius="wide"
            textCase="normal"
            onClick={onStart}
            disabled={isBusy || !canStart}
          >
            Start {trialLabel.adjective} Free Trial
          </FilledButton>

          <LegalLinksFooter
            idPrefix="free-trial-paywall"
            className="!pt-0 text-white"
            linkClassName="text-white hover:text-purple-100"
          />
        </div>
      </div>

      {backdrop}
    </div>
  );
}
